import React, { useState, useEffect } from "react";
import {
  FiSend,
  FiSettings,
  FiAlertTriangle,
  FiCheckCircle,

  FiRefreshCw,
} from "react-icons/fi";
import { Calendar } from "lucide-react";
import { useSelector } from "react-redux";
import subscriptionRequests from "../../api/Customer/SubscriptionRequests";

const SendingOptions = ({
  recipients,
  recipientCount,
  message,
  onSend,
  isLoading = false,
  sendDisabled = false,
  sendDisabledMessage = "", showScheduleButton = false,  // ADD THIS
  onScheduleClick,  

}) => {
  const user = useSelector((state) => state.user?.txtconnect_ng_user);
  const [availableUnits, setAvailableUnits] = useState(parseInt(user?.units) || 0);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const smsPartLength = 160;
  const messageLength = message.length;
  const smsParts = Math.ceil(messageLength / smsPartLength) || 1;
  const actualRecipientCount = recipientCount || recipients.length;
  const totalSms = actualRecipientCount * smsParts;

  const canSend = actualRecipientCount > 0 && message.trim().length > 0;
  const hasEnoughUnits = availableUnits >= totalSms;
  const canProceed = (canSend && hasEnoughUnits) && !sendDisabled;

  // Function to fetch live units from API
  const fetchLiveUnits = async () => {
    try {
      setLoadingUnits(true);
      const response = await subscriptionRequests.getCurrentUserSubscriptionData();
      
      if (response.status_code === "200" && response.data) {
        let unitsFromAPI = 0;
        
        if (response.data.data?.units) {
          unitsFromAPI = response.data.data.units;
        } else if (response.data.remaining_units) {
          unitsFromAPI = response.data.remaining_units;
        } else if (response.data.sms_balance) {
          unitsFromAPI = response.data.sms_balance;
        } else if (response.data.units) {
          unitsFromAPI = response.data.units;
        } else if (response.data.data?.sms_balance) {
          unitsFromAPI = response.data.data.sms_balance;
        }
        
        const newUnits = parseInt(unitsFromAPI) || 0;
        setAvailableUnits(newUnits);
        setLastUpdated(new Date());
        
        console.log('SendingOptions units updated:', newUnits);
      } else {
        setAvailableUnits(parseInt(user?.units) || 0);
      }
    } catch (error) {
      console.error("Error fetching units:", error);
      setAvailableUnits(parseInt(user?.units) || 0);
    } finally {
      setLoadingUnits(false);
    }
  };

  // Fetch units on component mount
  useEffect(() => {
    fetchLiveUnits();
  }, []);

  // Listen for page visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchLiveUnits();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Listen for Redux store updates
  useEffect(() => {
    if (user?.units) {
      const userUnits = parseInt(user.units) || 0;
      if (userUnits !== availableUnits) {
        setAvailableUnits(userUnits);
      }
    }
  }, [user?.units]);

  const handleSend = () => {
    if (sendDisabled) return;
    if (!canProceed) return;

    const sendData = {
      recipients,
      message,
      totalSms,
    };

    onSend && onSend(sendData);
    
    // Refresh units after sending
    setTimeout(() => {
      fetchLiveUnits();
    }, 1000); // Wait 1 second before refreshing
  };

  // Calculate unit status
  const getUnitStatusColor = () => {
    if (availableUnits === 0) return "text-red-600 bg-red-50 border-red-200";
    if (availableUnits < totalSms)
      return "text-orange-600 bg-orange-50 border-orange-200";
    if (availableUnits < 50)
      return "text-yellow-600 bg-yellow-50 border-yellow-200";
    return "text-green-600 bg-green-50 border-green-200";
  };

  const getUnitStatusIcon = () => {
    if (availableUnits === 0)
      return <FiAlertTriangle className="text-red-500" />;
    if (availableUnits < totalSms)
      return <FiAlertTriangle className="text-orange-500" />;
    if (availableUnits < 50)
      return <FiAlertTriangle className="text-yellow-500" />;
    return <FiCheckCircle className="text-green-500" />;
  };

  // Format last updated time
  const formatLastUpdated = () => {
    if (!lastUpdated) return "Not updated yet";
    const now = new Date();
    const diffMs = now - lastUpdated;
    const diffSec = Math.floor(diffMs / 1000);
    
    if (diffSec < 60) return `${diffSec}s ago`;
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
    return `${Math.floor(diffSec / 3600)}h ago`;
  };

  return (
    <div className="p-6 bg-white rounded-2xl border border-gray-100 shadow-xl">
      <div className="flex items-center mb-6 space-x-3">
        <div>
          <h3 className="text-xl font-bold text-gray-800">Campaign Summary </h3>
          <p className="text-sm text-gray-600"></p>
        </div>
      </div>

      {/* Unit Balance Alert - Auto-refreshing */}
      <div className="p-4 mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2">
              {getUnitStatusIcon()}
              <div>
                <div className="font-semibold text-blue-700">SMS Units Available</div>
                <div className="text-xs text-blue-600 opacity-80">
                  Auto-updates when you return to page
                </div>
              </div>
            </div>
          </div>
          <div className="text-right flex items-center gap-3">
            <div>
              <div className="text-lg font-bold text-blue-700">{availableUnits}</div>
              <div className="text-xs text-blue-600 opacity-80">
                {lastUpdated && `Updated ${formatLastUpdated()}`}
              </div>
            </div>
            <button
              onClick={fetchLiveUnits}
              disabled={loadingUnits}
              className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded-lg transition-colors disabled:opacity-50"
              title="Refresh units"
            >
              <FiRefreshCw className={`w-4 h-4 ${loadingUnits ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {!hasEnoughUnits && availableUnits > 0 && (
          <div className="p-3 mt-3 rounded-lg border bg-white/50 border-orange-200">
            <div className="text-sm font-medium text-orange-700">
              Need {totalSms - availableUnits} more units to send this message
            </div>
            <div className="mt-1 text-xs text-orange-600 opacity-80">
              Consider upgrading your plan or reducing recipients
            </div>
          </div>
        )}

        {availableUnits === 0 && (
          <div className="p-3 mt-3 rounded-lg border bg-white/50 border-red-200">
            <div className="text-sm font-medium text-red-700">No SMS units available</div>
            <div className="mt-1 text-xs text-red-600 opacity-80">
              Please purchase a subscription plan to continue sending messages
            </div>
          </div>
        )}
      </div>

      {/* Message Summary */}
      <div className="p-4 mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div className="p-3 bg-white rounded-lg border border-blue-100">
            <div className="font-medium text-blue-600">Recipients</div>
            <div className="text-lg font-bold text-gray-800">
              {actualRecipientCount}
            </div>
          </div>
          <div className="p-3 bg-white rounded-lg border border-blue-100">
            <div className="font-medium text-blue-600">Total SMS</div>
            <div className="text-lg font-bold text-gray-800">{totalSms}</div>
          </div>
        </div>

        <div className="mt-3 text-sm text-blue-700">
          {smsParts} SMS part{smsParts > 1 ? "s" : ""} × {actualRecipientCount}{" "}
          recipient{actualRecipientCount > 1 ? "s" : ""} = {totalSms} SMS units
        </div>
      </div>

      {/* Send Button */}
      <button
        id="sidebar-send-button"
        type="button"
        onClick={(e) => {
          if (sendDisabled || !canProceed || isLoading) {
            e.preventDefault();
            e.stopPropagation();
            return;
          }
          handleSend();
        }}
        disabled={sendDisabled || !canProceed || isLoading}
        aria-disabled={sendDisabled || !canProceed || isLoading}
        className={`w-full py-4 rounded-xl font-semibold text-lg flex items-center justify-center space-x-3 transition-all transform ${
          canProceed && !isLoading && !sendDisabled
            ? "bg-[#ee3636] hover:from-red-500 hover:to-[#ee3636] text-white shadow-lg hover:shadow-xl hover:scale-105 relative overflow-hidden"
            : "bg-gray-300 text-gray-500 cursor-not-allowed pointer-events-auto"
        }`}
        style={sendDisabled || !canProceed || isLoading ? { pointerEvents: "none" } : undefined}
      >
        {isLoading ? (
          <>
            <div className="w-6 h-6 rounded-full border-b-2 border-white animate-spin"></div>
            <span>Sending...</span>
          </>
        ) : (
          <>
            <FiSend className="text-xl" />
            <span>Send Now</span>
          </>
          
        )}
      </button>
  {showScheduleButton && (
  <button
    onClick={onScheduleClick}
    disabled={sendDisabled || !canProceed || isLoading}
    className={`w-full mt-3 py-4 rounded-xl font-semibold text-lg flex items-center justify-center space-x-3 transition-all transform ${
      canProceed && !isLoading && !sendDisabled
        ? "bg-white border-2 border-[#ee3636] text-[#ee3636] hover:bg-red-50 hover:scale-105"
        : "bg-gray-100 border-2 border-gray-300 text-gray-400 cursor-not-allowed"
    }`}
  >
    <Calendar className="w-5 h-5" />
    <span>Schedule SMS</span>
  </button>
)}

      

      {(!canProceed || sendDisabled) && (
        <div className="mt-3 text-sm text-center text-gray-500">
          {sendDisabled && sendDisabledMessage
            ? sendDisabledMessage
            : !canSend
            ? actualRecipientCount === 0 && message.trim().length === 0
              ? "Add recipients and write a message to send"
              : actualRecipientCount === 0
              ? "Add at least one recipient"
              : "Write your message"
            : !hasEnoughUnits
            ? `Insufficient units. Need ${totalSms} units, have ${availableUnits}`
            : "Unable to send message"}
        </div>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-3 gap-4 pt-4 mt-6 border-t border-gray-200">
        <div className="text-center">
          <div className="text-lg font-bold text-gray-800">
            {actualRecipientCount}
          </div>
          <div className="text-xs text-gray-500">Recipients</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-bold text-gray-800">{smsParts}</div>
          <div className="text-xs text-gray-500">SMS Page</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-bold text-gray-800">
            {loadingUnits ? (
              <div className="inline-block w-6 h-6 bg-gray-300 rounded-full animate-pulse"></div>
            ) : (
              availableUnits
            )}
          </div>
          <div className="text-xs text-gray-500">
            {loadingUnits ? 'Loading...' : 'Units Left'}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SendingOptions;